/* Klaris theme — core behaviour */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const money = (cents) => {
    const format = window.themeMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  };

  function toast(message) {
    const root = $('#ToastRoot');
    if (!root) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    root.appendChild(el);
    setTimeout(() => el.remove(), 3200);
  }

  /* ------------------------------------------------------------ cart core */

  const Cart = {
    async add(formData) {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Could not add to cart');
      }
      return res.json();
    },

    async change(line, quantity) {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      return res.json();
    },

    async get() {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      return res.json();
    },

    async refreshDrawer() {
      const url = `${window.Shopify.routes.root}?section_id=cart-drawer`;
      const res = await fetch(url);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      /* The Section Rendering API returns the section by itself, wrapped in
         #shopify-section-cart-drawer. It never contains the drawer shell from
         snippets/cart-drawer.liquid, so match the section — matching the shell
         finds nothing and the drawer silently keeps its stale contents. */
      const fresh = $('#shopify-section-cart-drawer', html);
      const current = $('#shopify-section-cart-drawer');
      const shell = $('[data-cart-drawer-inner]');
      if (fresh && current) {
        current.outerHTML = fresh.outerHTML;
      } else if (fresh && shell) {
        shell.innerHTML = fresh.outerHTML;
      }
      const cart = await Cart.get();
      Cart.updateCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    },

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }
  };

  window.KlarisCart = Cart;

  /* ---------------------------------------------------------- cart drawer */

  const Drawer = {
    open() {
      const drawer = $('[data-cart-drawer]');
      if (!drawer) return;
      drawer.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      const closeBtn = $('[data-drawer-close]', drawer);
      if (closeBtn) closeBtn.focus();
    },
    close() {
      const drawer = $('[data-cart-drawer]');
      if (!drawer) return;
      drawer.removeAttribute('open');
      document.body.style.overflow = '';
    }
  };

  window.KlarisDrawer = Drawer;

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-drawer-close]') || e.target.closest('[data-drawer-overlay]')) {
      Drawer.close();
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && $('[data-cart-drawer]')) {
      e.preventDefault();
      Drawer.open();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Drawer.close();
  });

  /* -------------------------------------------------------- product forms */

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    const button = $('[data-add-to-cart]', form);
    const label  = button ? $('[data-atc-label]', button) : null;
    const original = label ? label.textContent : '';

    if (button) {
      button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = button.dataset.loadingText || 'Adding…';
    }

    try {
      await Cart.add(new FormData(form));
      if ($('[data-cart-drawer]')) {
        await Cart.refreshDrawer();
        Drawer.open();
      } else {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }
    } catch (err) {
      toast(err.message);
    } finally {
      if (button) {
        button.removeAttribute('aria-disabled');
        if (label) label.textContent = original;
      }
    }
  });

  /* ------------------------------------------------ cart line item change */

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    const line = parseInt(btn.dataset.line, 10);
    const qty  = parseInt(btn.dataset.lineChange, 10);
    btn.setAttribute('aria-disabled', 'true');
    await Cart.change(line, qty);
    await Cart.refreshDrawer();
  });

  /* ------------------------------------------------------ product gallery */

  /* The stage is a native scroll-snap track, so a swipe needs no JS at all.
     JS only keeps the thumbs and dots in step with wherever the track lands. */

  function galleryShow(gallery, index) {
    const track = $('[data-gallery-track]', gallery);
    const slide = $(`[data-gallery-slide="${index}"]`, gallery);
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  }

  function gallerySync(gallery) {
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;

    /* Whichever slide covers the middle of the track is the one on screen. */
    const mid = track.scrollLeft + track.clientWidth / 2;
    let index = 0;
    $$('[data-gallery-slide]', track).forEach((slide) => {
      if (slide.offsetLeft - track.offsetLeft <= mid) index = +slide.dataset.gallerySlide;
    });

    $$('[data-gallery-thumb]', gallery).forEach((t) => {
      const on = +t.dataset.galleryThumb === index;
      t.setAttribute('aria-current', on ? 'true' : 'false');
      /* Keep the active thumb reachable on phones, where the strip scrolls. */
      if (on && t.parentElement && t.parentElement.scrollWidth > t.parentElement.clientWidth) {
        const strip = t.parentElement;
        const left = t.offsetLeft - strip.offsetLeft - (strip.clientWidth - t.clientWidth) / 2;
        strip.scrollTo({ left, behavior: 'smooth' });
      }
    });

    $$('[data-gallery-dot]', gallery).forEach((d) => {
      d.setAttribute('aria-current', +d.dataset.galleryDot === index ? 'true' : 'false');
    });
  }

  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    const gallery = thumb.closest('[data-gallery]');
    if (!gallery) return;
    galleryShow(gallery, thumb.dataset.galleryThumb);
  });

  function initGallery() {
    $$('[data-gallery]').forEach((gallery) => {
      const track = $('[data-gallery-track]', gallery);
      if (!track || track.dataset.galleryReady) return;
      track.dataset.galleryReady = 'true';

      let queued = false;
      track.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; gallerySync(gallery); });
      }, { passive: true });

      /* Arrow keys once the track has focus. */
      track.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const current = $$('[data-gallery-dot]', gallery)
          .findIndex((d) => d.getAttribute('aria-current') === 'true');
        const total = $$('[data-gallery-slide]', track).length;
        const next = e.key === 'ArrowRight'
          ? Math.min(current + 1, total - 1)
          : Math.max(current - 1, 0);
        if (next === current) return;
        e.preventDefault();
        galleryShow(gallery, next);
      });

      gallerySync(gallery);
    });
  }

  /* ------------------------------------------------------ variant picker */

  document.addEventListener('change', (e) => {
    const picker = e.target.closest('[data-variant-picker]');
    if (!picker) return;

    const root = picker.closest('[data-product-root]');
    if (!root) return;

    const variants = JSON.parse($('[data-variant-json]', root).textContent);
    const selected = $$('[data-variant-picker] input:checked, [data-variant-picker] select', root)
      .map((input) => input.value);

    const match = variants.find((v) =>
      v.options.every((opt, i) => opt === selected[i])
    );

    const idInput = $('[data-variant-id]', root);
    const button  = $('[data-add-to-cart]', root);
    const label   = button ? $('[data-atc-label]', button) : null;

    if (!match) {
      if (button) button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = window.themeStrings?.unavailable || 'Unavailable';
      return;
    }

    if (idInput) idInput.value = match.id;

    $$('[data-price-current]', root).forEach((el) => { el.textContent = money(match.price); });
    $$('[data-price-compare]', root).forEach((el) => {
      if (match.compare_at_price && match.compare_at_price > match.price) {
        el.textContent = money(match.compare_at_price);
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    if (button) {
      if (match.available) {
        button.removeAttribute('aria-disabled');
        if (label) label.textContent = window.themeStrings?.addToCart || 'Add to Cart';
      } else {
        button.setAttribute('aria-disabled', 'true');
        if (label) label.textContent = window.themeStrings?.soldOut || 'Sold out';
      }
    }
  });

  /* ------------------------------------------------------------ accordion */
  /* Optional single-open behaviour, opt in with data-accordion-single */

  document.addEventListener('toggle', (e) => {
    const item = e.target;
    if (!item.matches('details.accordion__item') || !item.open) return;
    const group = item.closest('[data-accordion-single]');
    if (!group) return;
    $$('details.accordion__item[open]', group).forEach((other) => {
      if (other !== item) other.open = false;
    });
  }, true);

  /* --------------------------------------------------------- sticky ATC */

  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const anchor = $('[data-atc-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });

    observer.observe(anchor);
  }

  /* ------------------------------------------------------------ marquee */
  /* Duplicates the track so the scroll loop has no visible seam */

  function initMarquee() {
    $$('[data-marquee]').forEach((el) => {
      if (el.dataset.marqueeReady) return;
      const track = $('[data-marquee-track]', el);
      if (!track) return;
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      el.appendChild(clone);
      el.dataset.marqueeReady = 'true';
    });
  }

  /* ------------------------------------------------------------- startup */

  function init() {
    initStickyAtc();
    initMarquee();
    initGallery();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page reload */
  document.addEventListener('shopify:section:load', init);
})();
